import { ReactNode } from 'react'
import { Alert, AlertColor, Box, IconButton, Slide, Snackbar, Tooltip } from '@mui/material'
import type { SlideProps, SxProps, Theme } from '@mui/material'
import { VolumeUp, VolumeOff, NotificationsActive, NotificationsOff } from '@mui/icons-material'
import {
  useNotificationPreferencesStore,
  NotificationCategoryKey,
} from '@store/notificationPreferencesStore'

interface NotificationToastProps {
  open: boolean
  message: ReactNode
  category: NotificationCategoryKey
  severity?: AlertColor
  autoHideDurationMs?: number
  /** Anchor — defaults to top center to match the existing toast style. */
  anchor?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'center' | 'right' }
  /** When true, the toast slides up from the bottom instead of the default grow. */
  slideUp?: boolean
  /** Extra style merged onto the Alert surface (e.g. to follow the app theme). */
  alertSx?: SxProps<Theme>
  /** Extra action (e.g. a "Review" button). Rendered alongside the toggles + close. */
  action?: ReactNode
  onClose: () => void
}

// Slide-up transition for the bottom-anchored journal toast.
function SlideUpTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />
}

/**
 * Snackbar/Alert wrapper that embeds two small icon toggles in the toast's
 * action area — sound on/off and popup on/off — bound to the per-user
 * notification preferences store for this category. Tapping the popup toggle
 * inside an open toast immediately closes it AND silences future popups for
 * this category; the sound toggle just flips the preference without closing.
 *
 * The MainLayout code that opens this toast is also expected to check the
 * popup preference before firing — this toggle is the user's "stop showing
 * me these" affordance once the toast is already on screen.
 */
export default function NotificationToast({
  open,
  message,
  category,
  severity = 'info',
  autoHideDurationMs = 8000,
  anchor = { vertical: 'top', horizontal: 'center' },
  slideUp = false,
  alertSx,
  action,
  onClose,
}: NotificationToastProps) {
  const prefs = useNotificationPreferencesStore((s) => s[category])
  const togglePopup = useNotificationPreferencesStore((s) => s.toggleCategoryPopup)
  const toggleSound = useNotificationPreferencesStore((s) => s.toggleCategorySound)

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDurationMs}
      onClose={onClose}
      anchorOrigin={anchor}
      TransitionComponent={slideUp ? SlideUpTransition : undefined}
    >
      <Alert
        onClose={onClose}
        severity={severity}
        variant="filled"
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Tooltip title={prefs.sound ? 'Notification sounds: on' : 'Notification sounds: off'}>
              <IconButton
                size="small"
                color="inherit"
                aria-label="Toggle notification sound"
                onClick={(e) => {
                  e.stopPropagation()
                  toggleSound(category)
                }}
              >
                {prefs.sound ? <VolumeUp fontSize="small" /> : <VolumeOff fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title={prefs.popup ? 'Notification popups: on' : 'Notification popups: off'}>
              <IconButton
                size="small"
                color="inherit"
                aria-label="Toggle notification popup"
                onClick={(e) => {
                  e.stopPropagation()
                  togglePopup(category)
                  if (prefs.popup) {
                    // User just turned popups off — dismiss the current toast
                    // so the action matches the intent immediately.
                    onClose()
                  }
                }}
              >
                {prefs.popup ? <NotificationsActive fontSize="small" /> : <NotificationsOff fontSize="small" />}
              </IconButton>
            </Tooltip>
            {action}
          </Box>
        }
        sx={{ width: '100%', ...alertSx }}
      >
        {message}
      </Alert>
    </Snackbar>
  )
}
